import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetAttendanceSettings,
  usePutAttendanceSettings,
  getGetAttendanceSettingsQueryKey,
  type AttendanceSettings
} from '@cdf/api-client-react';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { CheckIcon, Loader2Icon } from 'lucide-react';

/**
 * Admin-only attendance register settings: controls how voice recognition
 * behaves during roll call and sign-out. Loads from GET /api/settings/attendance,
 * saves via PUT /api/settings/attendance.
 */
export function SettingsAttendancePage() {
  const queryClient = useQueryClient();
  const { data, isPending } = useGetAttendanceSettings();
  const mutation = usePutAttendanceSettings();

  const [voiceEnabled, setVoiceEnabled] = React.useState(true);
  const [loaded, setLoaded] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  // Hydrate local state once when settings arrive
  React.useEffect(() => {
    if (data && !loaded) {
      setVoiceEnabled(data.voiceEnabled);
      setLoaded(true);
    }
  }, [data, loaded]);

  const handleSave = async () => {
    const settings: AttendanceSettings = {
      voiceEnabled
    };
    await mutation.mutateAsync({ data: settings });
    await queryClient.invalidateQueries({ queryKey: getGetAttendanceSettingsQueryKey() });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  if (isPending) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Attendance Register</CardTitle>
          <CardDescription>Loading settings…</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Attendance Register</CardTitle>
        <CardDescription>
          Control voice recognition for the mobile register view.
          Voice marking is always two-step: say a name to select, then say
          "Ok" or tap to confirm.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6 sm:max-w-lg">
        {/* Voice enabled toggle */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="voice-enabled">Voice recognition</Label>
            <p className="text-muted-foreground text-sm">
              Enable or disable the microphone toggle in the register view.
              Swipe-based marking always works regardless of this setting.
            </p>
          </div>
          <Switch
            id="voice-enabled"
            checked={voiceEnabled}
            onCheckedChange={setVoiceEnabled}
          />
        </div>

        <Separator />

        {/* Save button */}
        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={mutation.isPending}>
            {mutation.isPending ? (
              <>
                <Loader2Icon className="size-4 animate-spin" />
                Saving…
              </>
            ) : (
              'Save changes'
            )}
          </Button>
          {saved && (
            <span className="flex items-center gap-1.5 text-sm font-medium text-success">
              <CheckIcon className="size-4" />
              Saved
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
